import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Section for Utilities and Context
import "./billProfile.css";
import { AuthContext } from '../utils/AuthContext';
import API from '../utils/API';
import BillContacts from '../components/BillContacts';
import BillTrackingModal from '../components/BillTrackingModal';
import BillVersionText from '../components/BillVersionText';
import PageTitle from '../components/PageTitle';
import BillLifecycleProgress from '../components/BillLifecycleProgress';

const VoteRow = ({ vote, formatVoteDate }) => {
    const [expanded, setExpanded] = useState(false);

    const { groupedVotes, detailCounts } = useMemo(() => {
        if (!vote.DetailVotes || vote.DetailVotes.length === 0) {
            return { groupedVotes: [], detailCounts: null };
        }
        const groups = { 'AYE': [], 'NOE': [], 'ABS': [] };
        const others = {};

        vote.DetailVotes.filter(dv => dv.motion_id === vote.motion_id).forEach(dv => {
            const code = dv.vote_code ? dv.vote_code.toUpperCase() : 'N/A';
            const name = dv.legislator_name;
            if (groups[code]) {
                groups[code].push(name);
            } else {
                if (!others[code]) others[code] = [];
                others[code].push(name);
            }
        });

        const result = [];
        ['AYE', 'NOE', 'ABS'].forEach(code => {
            if (groups[code].length > 0) {
                groups[code].sort((a, b) => a.localeCompare(b));
                result.push({ code, names: groups[code].join(', ') });
            }
        });

        Object.keys(others).sort().forEach(code => {
            others[code].sort((a, b) => a.localeCompare(b));
            result.push({ code, names: others[code].join(', ') });
        });

        return {
            groupedVotes: result,
            detailCounts: {
                ayes: groups['AYE'].length,
                noes: groups['NOE'].length,
                abstain: groups['ABS'].length,
            }
        };
    }, [vote.DetailVotes, vote.motion_id]);

    return (
        <React.Fragment>
            <div className="row">
                <small className='text-muted mt-2'>{formatVoteDate(vote.vote_date_time)}</small>
            </div>
            <div className="mb-2 pe-2 row">
                <div className='text-muted col-8 pe-0'>
                    <small><strong>{vote.LocationCode?.description}</strong> 
                        {/* <i class="bi bi-card-checklist"/> */}
                    </small>
                </div>
                <div className='col-4 pe-1 ps-4'>
                    <div className='row'>
                        <div className='text-muted col-4 px-0'>
                            <i className="bi bi-arrow-up-circle-fill bill-vote-ayes"></i>
                            <small> {detailCounts ? detailCounts.ayes : vote.ayes}</small>
                        </div>
                        <div className='text-muted col-4 px-0'>
                            <i className="bi bi-arrow-down-circle-fill bill-vote-noes"></i>
                            <small> {detailCounts ? detailCounts.noes : vote.noes}</small>
                        </div>
                        <div className='text-muted col-4 px-0'>
                            <i className="bi bi-x-circle bill-vote-abstain"></i>
                            <small> {detailCounts ? detailCounts.abstain : vote.abstain}</small>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mb-2 pe-2 row align-items-end">
                <div className='col-11 pe-0'>
                    <small className='text-muted bill-history-motion'>{vote.BillMotion?.motion_text}</small>
                </div>
                <div className='col-1 ps-1 bill-detail-vote text-end'>
                    <i 
                        className={`bi ${expanded ? 'bi-arrows-collapse' : 'bi-arrows-expand'}`} 
                        title={expanded ? "Click to hide full vote" : "Click to see full vote"}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExpanded(!expanded)}
                    />
                </div>
            </div>
            {expanded && (
                <div className="mb-3 px-3 pt-2 pb-1 bg-light">
                    {groupedVotes.length > 0 ? (
                        groupedVotes.map(g => (
                            <div key={g.code} className="row my-1">
                                <div className="col-2 fw-bold small text-muted px-2">{g.code}</div>
                                <div className="col-10 small text-muted">{g.names}</div>
                            </div>
                        ))
                    ) : (
                        <small className="text-muted fst-italic">No detailed vote records available.</small>
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

function renderFeedbackNotes(text) {
    if (!text?.trim()) return <em className="text-muted">No notes provided.</em>;

    // Group consecutive lines into paragraph blocks, but split out any individual
    // line that is itself a fully-quoted sentence — a quote and the respondent's own
    // comment are often only one line apart (not a blank-line paragraph break), so
    // detecting quotes per-line (rather than requiring an entire block to be quoted)
    // is needed to catch those cases.
    const blocks = [];
    let currentPara = [];
    const flushPara = () => {
        if (currentPara.length > 0) {
            blocks.push({ type: 'para', lines: currentPara });
            currentPara = [];
        }
    };

    text.split('\n').forEach(line => {
        const trimmed = line.trim();
        if (!trimmed) {
            flushPara();
            return;
        }
        if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
            flushPara();
            blocks.push({ type: 'quote', text: trimmed.slice(1, -1) });
        } else {
            currentPara.push(line);
        }
    });
    flushPara();

    return blocks.map((block, i) => (
        block.type === 'quote' ? (
            <blockquote key={i} className="bp-notes-quote">{block.text}</blockquote>
        ) : (
            <p key={i} className="bp-notes-para">
                {block.lines.map((line, j) => (
                    <span key={j}>{line}{j < block.lines.length - 1 && <br />}</span>
                ))}
            </p>
        )
    ));
}

export default function BillProfile() {
    const navigate = useNavigate();
    const { bill_id } = useParams();
    const { auth } = useContext(AuthContext);
    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [currentVersionId, setCurrentVersionId] = useState(null);
    const [relatedSections, setRelatedSections] = useState(null);
    const [analyses, setAnalyses] = useState([]);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
    // null = closed, 'personal' or team_id (number) = open for that tracker
    const [trackingModalTracker, setTrackingModalTracker] = useState(null);
    const [trackingData, setTrackingData] = useState(null);
    const [subjectTagData, setSubjectTagData] = useState(null);
    const [tagPopoverTeamId, setTagPopoverTeamId] = useState(null);
    const tagPopoverRef = useRef(null);
    const [showBillInfo, setShowBillInfo] = useState(false);
    const [clientFeedback, setClientFeedback] = useState([]);
    const [feedbackModal, setFeedbackModal] = useState(false);
    const [feedbackContactFilter, setFeedbackContactFilter] = useState('');
    const [feedbackVersionFilter, setFeedbackVersionFilter] = useState('');
    const [billHistory, setBillHistory] = useState([]);

    useEffect(() => {
        const fetchBill = async () => {
            try {
                const response = await axios.get(`/api/legislation/bills/${bill_id}`);
                const billData = response.data;
                setBill(billData);

                let versionToDisplay = billData.LatestVersion;
                if (billData.BillVersions && billData.BillVersions.length > 0) {
                    const sortedVersions = [...billData.BillVersions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    const latestInList = sortedVersions[0];

                    if (!billData.LatestVersion || billData.LatestVersion.bill_version_id !== latestInList.bill_version_id) {
                        const versionResponse = await axios.get(`/api/legislation/version/${latestInList.bill_version_id}`);
                        versionToDisplay = versionResponse.data;
                    }
                }
                setSelectedVersion(versionToDisplay);
                setCurrentVersionId(versionToDisplay?.bill_version_id || null);
            } catch (err) {
                console.error("Error fetching bill profile:", err);
                setError("Failed to load bill profile.");
            } finally {
                setLoading(false);
            }
            };

            if (bill_id) {
            fetchBill();
        }
    }, [bill_id]);

    useEffect(() => {
        if (!bill_id) return;
        axios.get(`/api/legislation/bills/${bill_id}/analyses`)
            .then(res => setAnalyses(res.data))
            .catch(err => console.error("Error fetching analyses:", err));
    }, [bill_id]);

    useEffect(() => {
        if (!currentVersionId) return;
        axios.get(`/api/legislation/version/${currentVersionId}/related-sections`)
            .then(res => setRelatedSections(res.data))
            .catch(err => console.error("Error fetching related sections:", err));
    }, [currentVersionId]);

    const fetchTrackingData = () => {
        if (!auth?.user?.user_id || !bill_id) return;
        axios.get(`/api/teams/tracking-modal/${bill_id}`, { params: { user_id: auth.user.user_id } })
            .then(res => setTrackingData(res.data))
            .catch(err => console.error("Error fetching tracking data:", err));
    };

    const fetchSubjectTagData = () => {
        if (!auth?.user?.user_id || !bill_id) return;
        API.getBillSubjectTagsAcrossTeams(bill_id, auth.user.user_id)
            .then(res => setSubjectTagData(res.data))
            .catch(err => console.error("Error fetching subject tag data:", err));
    };

    const handleAddSubjectTag = async (teamId, tagId) => {
        try {
            await API.addBillSubjectTag(teamId, bill_id, {
                tag_id: tagId,
                requesting_user_id: auth.user.user_id,
            });
            fetchSubjectTagData();
        } catch (err) {
            console.error("Error adding subject tag:", err);
        }
    };

    const handleRemoveSubjectTag = async (teamId, tagId) => {
        try {
            await API.removeBillSubjectTag(teamId, bill_id, tagId, auth.user.user_id);
            fetchSubjectTagData();
        } catch (err) {
            console.error("Error removing subject tag:", err);
        }
    };

    const handleUntrack = async (trackerType, teamId = null, clientId = null) => {
        try {
            await API.untrackBill({
                user_id: auth.user.user_id,
                bill_id: bill.bill_id,
                tracker_type: trackerType,
                team_id: teamId,
                client_id: clientId,
            });
            fetchTrackingData();
        } catch (err) {
            console.error("Error removing tracking:", err);
        }
    };

    useEffect(() => {
        fetchTrackingData();
    }, [bill_id, auth?.user?.user_id]);

    useEffect(() => {
        fetchSubjectTagData();
    }, [bill_id, auth?.user?.user_id]);

    useEffect(() => {
        if (tagPopoverTeamId === null) return;
        const handleClickOutside = (e) => {
            if (tagPopoverRef.current && !tagPopoverRef.current.contains(e.target)) {
                setTagPopoverTeamId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [tagPopoverTeamId]);

    useEffect(() => {
        if (!bill_id || !auth?.user?.user_id) return;
        API.getBillClientFeedback(bill_id)
            .then(res => setClientFeedback(res.data))
            .catch(err => console.error('Error fetching client feedback:', err));
    }, [bill_id, auth?.user?.user_id]);

    useEffect(() => {
        if (!bill_id) return;
        API.getBillHistory(bill_id)
            .then(res => setBillHistory(res.data))
            .catch(err => console.error('Error fetching bill history:', err));
    }, [bill_id]);

    const formatBillTitle = (bill) => {
        const sessionNum = bill.session_number ? parseInt(bill.session_number) : 0;

        if (sessionNum > 0) {
            return (
                <>
                    <h4 className="bill-profile-number mb-2"><strong>{bill.bill_type}x{sessionNum} {bill.bill_number}</strong></h4>
                    <h6 className="bill-profile-title">{bill.title}</h6>
                </>            
            );
        }
        return (
            <>
                <h4 className="bill-profile-number mb-2"><strong>{bill.bill_type} {bill.bill_number}</strong></h4>
                <h6 className="bill-profile-title">{bill.title}</h6>
            </>            
        );
    };

    const formatSession = (session) => {
        if (!session) return '';
        if (session.length === 8) {
            return `Session ${session.substring(0, 4)}-${session.substring(4)}`;
        }
        return `Session ${session}`;
    }

    const formatVersionDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return `(${date.toLocaleDateString('en-US')})`;
    };

    const amendmentBadgeClass = (classification) => {
        if (classification === 'Gut-and-amend') return 'bp-amendment-badge bp-amendment-badge--gut-and-amend';
        if (classification === 'Substantive') return 'bp-amendment-badge bp-amendment-badge--substantive';
        if (classification === 'Non-substantive') return 'bp-amendment-badge bp-amendment-badge--non-substantive';
        return null;
    };

    const renderAmendmentBadge = (version) => (
        version?.amendment_classification
            ? <span className={amendmentBadgeClass(version.amendment_classification)}>{version.amendment_classification}</span>
            : null
    );

    const renderAmendmentShortLabel = (version) => {
        if (version?.amendment_classification === 'Gut-and-amend') {
            return <span className="bp-amendment-short bp-amendment-short--gut-and-amend" title="Gut-and-amend">G&amp;A</span>;
        }
        if (version?.amendment_classification === 'Substantive') {
            return <span className="bp-amendment-short bp-amendment-short--substantive" title="Substantive">S</span>;
        }
        return null;
    };

    const handleNavigate = (profile_id) => {
        navigate("/profile/" + profile_id);
    }

    const handleVersionSelect = async (version) => {
        if (bill.LatestVersion && version.bill_version_id === bill.LatestVersion.bill_version_id) {
            setSelectedVersion(bill.LatestVersion);
            return;
        }

        try {
            const response = await axios.get(`/api/legislation/version/${version.bill_version_id}`);
            setSelectedVersion(response.data);
        } catch (err) {
            console.error("Error fetching bill version:", err);
        }
    };

    const handleCompareVersions = () => {
        if (!bill?.BillVersions?.length) return;

        const sorted = [...bill.BillVersions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        const idx = selectedVersion ? sorted.findIndex(v => v.bill_version_id === selectedVersion.bill_version_id) : -1;
        const previous = idx > 0 ? sorted[idx - 1] : null;

        const params = new URLSearchParams();
        if (selectedVersion) params.set('left', selectedVersion.bill_version_id);
        if (previous) params.set('right', previous.bill_version_id);

        navigate(`/legislation/bill/${bill.bill_id}/compare${params.toString() ? `?${params.toString()}` : ''}`);
    };

    const formatVoteDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: 'UTC'
        });
    };

    const formatHearingDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };

    const formatAnalysisDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
    };

    const openAnalysisModal = async (analysis) => {
        try {
            const res = await axios.get(`/api/legislation/analyses/${analysis.analysis_id}`);
            setSelectedAnalysis(res.data);
            setAnalysisModalOpen(true);
        } catch (err) {
            console.error("Error fetching analysis:", err);
        }
    };

    const closeAnalysisModal = () => {
        setAnalysisModalOpen(false);
        setSelectedAnalysis(null);
    };

    const ANALYSIS_DOC_STYLES = `
        body, div { font-family: 'Times New Roman', Times, serif; font-size: 11.5pt; color: #000; line-height: 1.5; }
        p { margin: 0 0 0.6em 0; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 0.75em; font-size: inherit; }
        td, th { padding: 3px 8px; vertical-align: top; font-size: inherit; }
        hr { border: none; border-top: 1.5px solid #000; margin: 10px 0; }
        h1, h2, h3, h4 { font-family: 'Times New Roman', Times, serif; font-size: 11.5pt; font-weight: bold; margin-bottom: 0.4em; }
        ul, ol { margin: 0.4em 0 0.6em 1.75em; padding: 0; }
        li { margin-bottom: 0.35em; }
        strong, b { font-weight: bold; }
        em, i { font-style: italic; }
    `;

    const downloadAnalysisAsWord = async (analysis) => {
        try {
            const res = await axios.get(`/api/legislation/analyses/${analysis.analysis_id}`);
            const html = res.data.analysis_html || '';
            const filename = `${analysis.committee_name || 'Analysis'} - ${formatAnalysisDate(analysis.analysis_date)}`;
            const wordHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>${filename}</title><style>${ANALYSIS_DOC_STYLES}</style></head><body style='margin:72pt 72pt 72pt 72pt;'>${html}</body></html>`;
            const blob = new Blob([wordHtml], { type: 'application/msword' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.doc`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading Word:", err);
        }
    };

    const downloadAnalysisAsPDF = async (analysis) => {
        try {
            const res = await axios.get(`/api/legislation/analyses/${analysis.analysis_id}`);
            const html = res.data.analysis_html || '';
            const filename = `${analysis.committee_name || 'Analysis'} - ${formatAnalysisDate(analysis.analysis_date)}`;
            const win = window.open('', '_blank');
            win.document.write(`<html><head><title>${filename}</title><style>${ANALYSIS_DOC_STYLES} @media print { body { margin: 0.75in; } }</style></head><body style='margin:0.75in;'>${html}</body></html>`);
            win.document.close();
            win.focus();
            win.print();
        } catch (err) {
            console.error("Error downloading PDF:", err);
        }
    };

    const sortedBillVersions = bill?.BillVersions
        ? [...bill.BillVersions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        : [];

    const feedbackContactOptions = Array.from(
        new Map(clientFeedback.map(resp => [resp.contact_id, resp.respondent_name])).entries()
    ).sort((a, b) => a[1].localeCompare(b[1]));

    const getVersionDateRange = (versionId) => {
        const idx = sortedBillVersions.findIndex(v => v.bill_version_id === versionId);
        if (idx === -1) return null;
        return {
            start: new Date(sortedBillVersions[idx].createdAt).getTime(),
            end: idx + 1 < sortedBillVersions.length ? new Date(sortedBillVersions[idx + 1].createdAt).getTime() : null,
        };
    };

    const filteredClientFeedback = clientFeedback.filter(resp => {
        if (feedbackContactFilter && String(resp.contact_id) !== feedbackContactFilter) return false;
        if (feedbackVersionFilter) {
            const range = getVersionDateRange(feedbackVersionFilter);
            const submittedAt = new Date(resp.submitted_at).getTime();
            if (range && submittedAt < range.start) return false;
            if (range && range.end !== null && submittedAt >= range.end) return false;
        }
        return true;
    });

    const clearFeedbackFilters = () => {
        setFeedbackContactFilter('');
        setFeedbackVersionFilter('');
    };

    const authorData = useMemo(() => {
        if (selectedVersion?.BillVersionAuthors?.length) {
            const primary = selectedVersion.BillVersionAuthors.find(a => a.primary_author_flag === 'Y') || selectedVersion.BillVersionAuthors[0];
            if (primary?.Legislator) { 
                const { Legislator } = primary;
                const { ProfileInfo } = Legislator;
                const currentJob = Legislator.JobExperiences?.find(j => j.current_job);
                return {
                    first_name: ProfileInfo?.first_name || Legislator.first_name,
                    last_name: ProfileInfo?.last_name || Legislator.last_name,
                    profile_picture: ProfileInfo?.profile_picture,
                    district: Legislator.district,
                    profile_id: ProfileInfo?.profile_id,
                    organization_id: currentJob?.organization_id
                };
            } else if (primary?.author_name) {
                return {
                    first_name: primary.author_name,
                    last_name: '',
                    profile_picture: null,
                    district: null,
                    profile_id: null,
                    organization_id: null
                };
            }
        }
        
        if (bill?.Author) {
            const currentJob = bill.Author.JobExperiences?.find(j => j.current_job);
             return {
                first_name: bill.Author.first_name,
                last_name: bill.Author.last_name,
                profile_picture: bill.Author.profile_picture,
                district: null,
                profile_id: bill.Author.profile_id,
                organization_id: currentJob?.organization_id
            };
        }
        return null;
    }, [selectedVersion, bill]);

    const currentHearings = useMemo(() => {
        if (!bill?.CommitteeHearings || !bill?.location) return [];

        // Use string comparison on YYYY-MM-DD to avoid the JS Date UTC-parse trap
        // where new Date("2026-04-21") = UTC midnight = previous day at 5pm PT
        const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

        const hearings = bill.CommitteeHearings.filter(h => {
            const dateStr = (h.CommitteeAgenda?.agenda_date || h.hearing_date || '').split('T')[0];
            return h.committee_code === bill.location && dateStr >= todayStr;
        });

        if (hearings.length === 0) return [];

        hearings.sort((a, b) => {
            const da = (a.hearing_date || '').split('T')[0];
            const db = (b.hearing_date || '').split('T')[0];
            return da < db ? -1 : da > db ? 1 : 0;
        });

        return [hearings[0]];
    }, [bill]);

    if (loading) return <div className="d-flex position-absolute top-50 start-50 translate-middle">
              <div className="spinner-border loading-spinner" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>;
    if (error) return <div className="container mt-5 text-center text-danger">{error}</div>;
    if (!bill) return <div className="container mt-5 text-center">Bill not found.</div>;

    const sessionNum = bill.session_number ? parseInt(bill.session_number) : 0;
    const tabTitle = `${bill.bill_type}${sessionNum > 0 ? `x${sessionNum}` : ''} ${bill.bill_number} | 'elloGovna`;

    return (
        <div className="container bill-profile-page">
            <PageTitle title={tabTitle} />
            <div className="row mb-3 align-items-center">
                <div className="col-md-8 bp-main-col">
                    <span className="bill-profile-title mb-0">{formatBillTitle(bill)}</span>
                </div>
                <div className="col-md-4 bp-side-col text-md-end text-start mt-2 mt-md-0">
                    <h5 className="text-muted mb-0">{formatSession(bill.session)}</h5>
                    <button
                        className="bp-tablet-info-btn mt-1"
                        onClick={() => setShowBillInfo(v => !v)}
                    >
                        {showBillInfo ? 'Show Bill Text' : 'Show Bill Info'}&nbsp;
                        <i className={`bi bi-chevron-${showBillInfo ? 'up' : 'down'}`} />
                    </button>
                </div>
            </div>

            {/* Version card shown above bill text at tablet — hidden at desktop */}
            <div className="bp-version-above mb-3">
                <div className="card">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-6 border-end">
                                <small className="text-muted d-block">Selected Version:</small>
                                <strong>
                                    {selectedVersion
                                        ? `${selectedVersion.version_status} ${formatVersionDate(selectedVersion.createdAt)}`
                                        : 'N/A'}
                                </strong>
                                {renderAmendmentBadge(selectedVersion)}
                            </div>
                            <div className="col-6">
                                <small className="text-muted d-block">Other Versions:</small>
                                <div className="other-versions-list" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    {bill.BillVersions && bill.BillVersions
                                        .filter(v => v.bill_version_id !== selectedVersion?.bill_version_id)
                                        .map(version => (
                                            <div key={version.bill_version_id}>
                                                <span className="bill-link" style={{ cursor: 'pointer' }} onClick={() => handleVersionSelect(version)}>
                                                    {version.version_status} {formatVersionDate(version.createdAt)}
                                                </span>
                                                {renderAmendmentShortLabel(version)}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                        {bill.BillVersions && bill.BillVersions.length > 1 && (
                            <div className="mt-2 bp-compare-versions-link">
                                <button className="btn bp-compare-versions-btn" onClick={handleCompareVersions}>
                                    <i className="bi bi-file-earmark-diff"></i>Compare Versions
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Left Column - Bill Text */}
                <div className={`col-md-8 bp-main-col order-2 order-md-1 bp-tablet-bill-text${showBillInfo ? ' hide' : ''}`}>
                    <div className="card mb-4 bp-bill-text-card" style={{ minHeight: '800px' }}>
                        {selectedVersion && (
                            <a
                                className="bp-bill-pdf-download bill-link"
                                href={`https://leginfo.legislature.ca.gov/faces/billPdf.xhtml?bill_id=${bill.bill_id}&version=${selectedVersion.bill_version_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Download PDF of this version"
                            >
                                <i className="bi bi-file-earmark-pdf" />
                            </a>
                        )}
                        <div className="card-body">
                            <div className="bill-content">
                                <BillVersionText bill={bill} version={selectedVersion} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - Info Cards */}
                <div className={`col-md-4 bp-side-col order-1 order-md-2 mb-3 bp-tablet-info-col${showBillInfo ? ' show' : ''}`}>

                    {/* Selected Version / Other Versions Placeholder */}
                    <div className="card mb-3 bp-version-in-col">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-6 border-end">
                                    <small className="text-muted d-block">Selected Version:</small>
                                    <strong>
                                        {selectedVersion
                                            ? `${selectedVersion.version_status} ${formatVersionDate(selectedVersion.createdAt)}`
                                            : 'N/A'}
                                    </strong>
                                    {renderAmendmentBadge(selectedVersion)}
                                </div>
                                <div className="col-6">
                                    <small className="text-muted d-block">Other Versions:</small>
                                    <div className="other-versions-list" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {bill.BillVersions && bill.BillVersions
                                            .filter(v => v.bill_version_id !== selectedVersion?.bill_version_id)
                                            .map(version => (
                                                <div key={version.bill_version_id}>
                                                    <span className="bill-link" style={{cursor: 'pointer'}} onClick={() => handleVersionSelect(version)}>
                                                        {version.version_status} {formatVersionDate(version.createdAt)}
                                                    </span>
                                                    {renderAmendmentShortLabel(version)}
                                                </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {bill.BillVersions && bill.BillVersions.length > 1 && (
                                <div className="mt-2 bp-compare-versions-link">
                                    <button className="btn bp-compare-versions-btn" onClick={handleCompareVersions}>
                                        <i className="bi bi-file-earmark-diff"></i>Compare Versions
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Author + Status Combined Card */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                                {authorData?.profile_picture && (
                                    <img
                                        src={authorData.profile_picture}
                                        alt="Author"
                                        className="author-card-picture me-3"
                                        onClick={() => handleNavigate(authorData.profile_id)}
                                    />
                                )}
                                <div>
                                    <div className="bp-section-label">Author</div>
                                    <h6
                                        className="author-card-name mb-0"
                                        onClick={() => authorData?.profile_id && handleNavigate(authorData.profile_id)}
                                    >
                                        {authorData ? `${authorData.first_name} ${authorData.last_name}` : 'N/A'}
                                    </h6>
                                    <small className="text-muted">{authorData?.district || ''}</small>
                                </div>
                            </div>
                            <hr className="my-2" />
                            {bill.status === 'Chaptered' && bill.chapter_number && bill.chapter_year ? (
                                <div className="row g-2 mt-1">
                                    <div className="col-12">
                                        <div className="bp-section-label">Chaptering</div>
                                        <div className="small">
                                            Chapter {bill.chapter_number}, Statutes of {bill.chapter_year}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="row g-2 mt-1">
                                    <div className="col-6">
                                        <div className="bp-section-label">Location</div>
                                        <div className="small" title={bill.LocationCode ? bill.LocationCode.description : (bill.location || '')}>
                                            {bill.LocationCode ? bill.LocationCode.description : (bill.location || 'N/A')}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="bp-section-label">Hearing</div>
                                        {currentHearings.length > 0 ? (
                                            currentHearings.map((hearing, index) => (
                                                <div key={index} className="small">
                                                    {hearing.CommitteeAgenda ? (
                                                        <>
                                                            {formatHearingDate(hearing.CommitteeAgenda.agenda_date)}
                                                            {hearing.CommitteeAgenda.agenda_time && ` at ${hearing.CommitteeAgenda.agenda_time}`}
                                                            <br />
                                                            {hearing.CommitteeAgenda.loc_line_1}
                                                        </>
                                                    ) : (
                                                        <span>{formatHearingDate(hearing.hearing_date)}</span>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="small">None Scheduled</div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <br />
                            {/* <hr className="my-3" /> */}
                            <BillLifecycleProgress bill={bill} billHistory={billHistory} />
                        </div>
                    </div>

                    {/* AI Summary — bill summary plus the AI amendment analysis of the selected version */}
                    {(bill.ai_summary || selectedVersion?.amendment_summary) && (
                        <div className="card mb-3 bp-ai-summary-card">
                            <div className="card-header bp-ai-summary-header">
                                <i className="bi bi-stars me-2" />
                                <strong>AI Summary</strong>
                            </div>
                            <div className="card-body">
                                {bill.ai_summary && (
                                    <p className="bp-ai-summary-text mb-0">{bill.ai_summary.replace(/^#+\s+[^\n]*\n*/g, '').trim()}</p>
                                )}
                                {selectedVersion?.amendment_summary && (
                                    <div className={bill.ai_summary ? 'bp-amendment-section' : undefined}>
                                        <div className="bp-amendment-subheader">
                                            <span className="bp-section-label mb-0">Amendment Analysis</span>
                                            {renderAmendmentBadge(selectedVersion)}
                                        </div>
                                        <p className="bp-amendment-text mb-2">{selectedVersion.amendment_summary}</p>
                                        {selectedVersion.amendment_reasoning && (
                                            <p className="bp-amendment-reasoning mb-1">{selectedVersion.amendment_reasoning}</p>
                                        )}
                                        {selectedVersion.amendment_confidence && (
                                            <small className="text-muted">Confidence: {selectedVersion.amendment_confidence}</small>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <BillContacts
                        billId={bill.bill_id}
                        billAuthorOrgId={authorData?.organization_id}
                    />
                    
                    {/* Tracking Card */}
                    {auth?.user?.user_id && (
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-white d-flex align-items-center justify-content-between">
                                <strong>Tracking</strong>
                                <i
                                    className="bi bi-plus-circle bill-link"
                                    title="Add tracking"
                                    style={{ cursor: 'pointer', fontSize: '1rem' }}
                                    onClick={() => setTrackingModalTracker('personal')}
                                />
                            </div>
                            <div className="card-body pt-2 pb-2">
                                {(() => {
                                    if (!trackingData) return null;

                                    const posColor = (pos) => {
                                        if (!pos) return '';
                                        if (pos.startsWith('Support')) return 'track-pos-support';
                                        if (pos.startsWith('Oppose')) return 'track-pos-oppose';
                                        if (pos === 'None') return 'track-pos-none';
                                        return 'track-pos-neutral';
                                    };
                                    const priColor = (pri) => {
                                        if (!pri) return '';
                                        if (pri === 'Critical') return 'track-pri-critical';
                                        if (pri === 'High') return 'track-pri-high';
                                        if (pri === 'Watch') return 'track-pri-watch';
                                        if (pri === 'Standard') return 'track-pri-standard';
                                        return 'track-pri-low';
                                    };

                                    const Badges = ({ position, priority, onEdit, onDelete, canManage = true }) => (
                                        <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                                            <div className="d-flex gap-1">
                                                {position && <span className={`tracking-badge ${posColor(position)}`}>{position}</span>}
                                                {priority && <span className={`tracking-badge ${priColor(priority)}`}>{priority}</span>}
                                            </div>
                                            {canManage && <i className="bi bi-pencil-square track-edit-icon" title="Edit" onClick={onEdit} />}
                                            {canManage && <i className="bi bi-trash3 track-delete-icon" title="Remove tracking" onClick={onDelete} />}
                                        </div>
                                    );

                                    const hasPersonal = trackingData.personal.active_track;
                                    const activeTeams = trackingData.teams.filter(t =>
                                        t.active_track || t.clients?.some(c => c.active_track)
                                    );

                                    if (!hasPersonal && activeTeams.length === 0) {
                                        return <p className="text-muted bp-placeholder-text fst-italic mb-0">Not currently tracking this bill.</p>;
                                    }


                                    return (
                                        <div className="tracking-list">
                                            {hasPersonal && (
                                                <div className="tracking-row">
                                                    <div className="d-flex align-items-start justify-content-between">
                                                        <span className="tracking-entry-label">
                                                            <i className="bi bi-person-fill me-1" />My Tracking
                                                        </span>
                                                        <Badges
                                                            position={trackingData.personal.position}
                                                            priority={trackingData.personal.priority}
                                                            onEdit={() => setTrackingModalTracker('personal')}
                                                            onDelete={() => handleUntrack('personal')}
                                                        />
                                                    </div>
                                                    {trackingData.personal.notes && <div className="tracking-notes">{trackingData.personal.notes}</div>}
                                                </div>
                                            )}
                                            {activeTeams.map(team => (
                                                <div key={team.team_id} className="tracking-row">
                                                    <div className="d-flex align-items-start justify-content-between">
                                                        <span className="tracking-entry-label">
                                                            <i className="bi bi-briefcase me-1" />{team.team_name}
                                                        </span>
                                                        {team.active_track && (
                                                            <Badges
                                                                position={team.position}
                                                                priority={team.priority}
                                                                canManage={team.bill_manage_permission}
                                                                onEdit={() => setTrackingModalTracker(team.team_id)}
                                                                onDelete={() => handleUntrack('team', team.team_id)}
                                                            />
                                                        )}
                                                    </div>
                                                    {team.active_track && team.notes && <div className="tracking-notes">{team.notes}</div>}
                                                    {team.clients?.filter(c => c.active_track).map(client => (
                                                        <div key={client.client_id} className="tracking-client-row">
                                                            <div className="d-flex align-items-start justify-content-between">
                                                                <span className="tracking-client-label">
                                                                    {client.client_name}
                                                                </span>
                                                                <Badges
                                                                    position={client.position}
                                                                    priority={client.priority}
                                                                    canManage={team.bill_manage_permission}
                                                                    onEdit={() => setTrackingModalTracker(team.team_id)}
                                                                    onDelete={() => handleUntrack('client', team.team_id, client.client_id)}
                                                                />
                                                            </div>
                                                            {client.notes && <div className="tracking-notes">{client.notes}</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Client Feedback Card */}
                    {auth?.user?.user_id && clientFeedback.length > 0 && (
                        <div className="card shadow-sm mb-3">
                            <div className="card-header bg-white d-flex align-items-center justify-content-between">
                                <strong>Client Feedback</strong>
                                <button
                                    className="bp-feedback-toggle"
                                    onClick={() => setFeedbackModal(true)}
                                >
                                    {clientFeedback.length === 1 ? '1 response' : `${clientFeedback.length} responses`}
                                    <i className="bi bi-arrow-right ms-1" />
                                </button>
                            </div>
                            <div className="card-body pt-2 pb-1 bp-feedback-preview">
                                {clientFeedback.slice(0, 3).map(resp => (
                                    <div key={resp.id} className="bp-feedback-row">
                                        <div className="bp-feedback-meta">
                                            <span className="bp-feedback-client">{resp.ClientFeedbackRequest?.TeamClient?.client_name}</span>
                                            <span className="bp-feedback-separator">·</span>
                                            <span className="bp-feedback-respondent">{resp.respondent_name}</span>
                                            <span className="bp-feedback-separator">·</span>
                                            <span className="bp-feedback-date">
                                                {new Date(resp.submitted_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                                            </span>
                                        </div>
                                        <p className="bp-feedback-notes bp-feedback-notes--clamp">{resp.notes || <em className="text-muted">No notes provided.</em>}</p>
                                    </div>
                                ))}
                                {clientFeedback.length > 3 && (
                                    <button
                                        className="bp-feedback-more"
                                        onClick={() => setFeedbackModal(true)}
                                    >
                                        + {clientFeedback.length - 3} more response{clientFeedback.length - 3 !== 1 ? 's' : ''} — view all
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Bill Analysis Card */}
                    <div className="card shadow-sm mb-3">
                        <div className="card-header bg-white">
                            <strong>Bill Analysis</strong>
                        </div>
                        <div className="card-body pt-1">
                            {analyses.length > 0 ? (
                                analyses.map((analysis, idx) => (
                                    <div key={analysis.analysis_id} className={`d-flex align-items-center justify-content-between py-2${idx < analyses.length - 1 ? ' border-bottom' : ''}`}>
                                        <small className="text-muted">
                                            {formatAnalysisDate(analysis.analysis_date)} - {analysis.committee_name || 'N/A'}
                                        </small>
                                        <div className="d-flex gap-2 ms-2 text-nowrap">
                                            <i
                                                className="bi bi-eye bill-link"
                                                title="View analysis"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => openAnalysisModal(analysis)}
                                            />
                                            <i
                                                className="bi bi-file-earmark-word bill-link"
                                                title="Download as Word"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => downloadAnalysisAsWord(analysis)}
                                            />
                                            <i
                                                className="bi bi-file-earmark-pdf bill-link"
                                                title="Download as PDF"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => downloadAnalysisAsPDF(analysis)}
                                            />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted text-center small mt-2">No analyses available.</p>
                            )}
                        </div>
                    </div>

                    {/* Vote History Card */}
                    <div className="card shadow-sm mb-3">
                        <div className="card-header bg-white">
                            <strong>Vote History</strong>
                            <span className="eg-tooltip-wrap ms-1">
                              <i className="bi bi-info-circle eg-tooltip-icon" />
                              <div className="eg-tooltip">To see a detailed vote record, click the expand icon next to each vote entry.</div>
                            </span>
                        </div>
                        <div className="card-body pt-1 bp-scroll-panel" style={{ maxHeight: '400px' }}>
                            <div>
                                {bill.SummaryVotes && bill.SummaryVotes.length > 0 ? (
                                    bill.SummaryVotes
                                    .sort((a, b) => new Date(b.vote_date_time) - new Date(a.vote_date_time))
                                    .map((vote, index, arr) => (
                                        <React.Fragment key={index}>
                                            <VoteRow
                                                vote={vote}
                                                formatVoteDate={formatVoteDate}
                                            />
                                            {index < arr.length - 1 && <hr className="my-1" />}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <p className="text-muted text-center small">No vote history available.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Bill History Card */}
                    <div className="card shadow-sm mb-3">
                        <div className="card-header bg-white">
                            <strong>Bill History</strong>
                        </div>
                        <div className="card-body pt-1 pb-1 bp-scroll-panel" style={{ maxHeight: '400px' }}>
                            {billHistory.length > 0 ? (
                                <div>
                                    {billHistory.map((item, index, arr) => (
                                        <React.Fragment key={item.bill_history_id}>
                                            <div className="bill-history-row">
                                                <small className="text-muted bill-history-date">
                                                    {formatVoteDate(item.action_date)}
                                                </small>
                                                <small className="bill-history-action">{item.action}</small>
                                            </div>
                                            {index < arr.length - 1 && <hr className="my-1" />}
                                        </React.Fragment>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted text-center small mt-2">No bill history available.</p>
                            )}
                        </div>
                    </div>

                    {/* Related Legislation (overlapping code sections, current bill version) */}
                    {relatedSections?.related_bills?.length > 0 && (
                        <div className="card shadow-sm mb-3 bp-related-sections-card">
                            <div className="card-header bg-white">
                                <strong>Related Legislation</strong>
                            </div>
                            <div className="card-body pt-1 pb-1 bp-scroll-panel" style={{ maxHeight: '260px' }}>
                                {relatedSections.related_bills.map((related, index, arr) => {
                                    const sectionsByCode = related.matches.reduce((acc, m) => {
                                        (acc[m.code_acronym] = acc[m.code_acronym] || []).push(m.section_number);
                                        return acc;
                                    }, {});
                                    return (
                                        <React.Fragment key={related.bill_id}>
                                            <div className="py-1">
                                                <div className="d-flex justify-content-between align-items-baseline">
                                                    <span
                                                        className="bill-link"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => navigate(`/legislation/bill/${related.bill_id}`)}
                                                    >
                                                        <strong>{related.bill_type} {related.bill_number}</strong>
                                                    </span>
                                                    {related.status && (
                                                        <small className="text-muted ms-2">{related.status}</small>
                                                    )}
                                                </div>
                                                {Object.entries(sectionsByCode).map(([code_acronym, sectionNums]) => (
                                                    <div key={code_acronym} className="small text-muted">
                                                        {code_acronym} &sect;{sectionNums.join(', ')}
                                                    </div>
                                                ))}
                                            </div>
                                            {index < arr.length - 1 && <hr className="my-1" />}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Subject Tags Card */}
                    {auth?.user?.user_id && subjectTagData?.teams?.length > 0 && (() => {
                        const teams = subjectTagData.teams;
                        const singleTeam = teams.length === 1 ? teams[0] : null;

                        const AddTagControl = ({ team }) => {
                            const availableTags = team.tags.filter(t => !team.attachedTagIds.includes(t.tag_id));
                            if (!team.canBill || availableTags.length === 0) return null;
                            return (
                                <div
                                    className="subject-tag-popover-wrap"
                                    ref={tagPopoverTeamId === team.team_id ? tagPopoverRef : null}
                                >
                                    <button
                                        className="btn btn-sm subject-tag-add-trigger"
                                        type="button"
                                        onClick={() => setTagPopoverTeamId(prev => prev === team.team_id ? null : team.team_id)}
                                    >
                                        <i className="bi bi-plus-lg me-1" />Add Tag
                                    </button>
                                    {tagPopoverTeamId === team.team_id && (
                                        <ul className="subject-tag-popover-menu">
                                            {availableTags.map(t => (
                                                <li key={t.tag_id}>
                                                    <button
                                                        className="subject-tag-popover-item"
                                                        type="button"
                                                        onClick={() => {
                                                            handleAddSubjectTag(team.team_id, t.tag_id);
                                                            setTagPopoverTeamId(null);
                                                        }}
                                                    >
                                                        {t.name}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            );
                        };

                        const TagChips = ({ team }) => {
                            const attachedTags = team.tags.filter(t => team.attachedTagIds.includes(t.tag_id));
                            if (attachedTags.length === 0) {
                                if (team.tags.length === 0) {
                                    return (
                                        <p className="text-muted bp-placeholder-text fst-italic mb-0">
                                            No tags created yet. Create tags in the Bill Work section on the Workspace page.
                                        </p>
                                    );
                                }
                                return <p className="text-muted bp-placeholder-text fst-italic mb-0">No tags selected yet.</p>;
                            }
                            return (
                                <div className="subject-tag-chip-list">
                                    {attachedTags.map(t => (
                                        <span key={t.tag_id} className="subject-tag-chip">
                                            {t.name}
                                            {team.canBill && (
                                                <i
                                                    className="bi bi-x subject-tag-remove-icon"
                                                    title="Remove tag"
                                                    onClick={() => handleRemoveSubjectTag(team.team_id, t.tag_id)}
                                                />
                                            )}
                                        </span>
                                    ))}
                                </div>
                            );
                        };

                        return (
                            <div className="card shadow-sm mb-3">
                                <div className="card-header bg-white d-flex align-items-center justify-content-between">
                                    <strong>Subject Tags</strong>
                                    {singleTeam && <AddTagControl team={singleTeam} />}
                                </div>
                                <div className="card-body pt-2 pb-2">
                                    {singleTeam ? (
                                        <TagChips team={singleTeam} />
                                    ) : (
                                        <div className="subject-tag-list">
                                            {teams.map(team => (
                                                <div key={team.team_id} className="subject-tag-row">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <span className="subject-tag-entry-label">
                                                            <i className="bi bi-briefcase me-1" />{team.team_name}
                                                        </span>
                                                        <AddTagControl team={team} />
                                                    </div>
                                                    <TagChips team={team} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                </div>
            </div>

            {/* Bill Tracking Modal */}
            {trackingModalTracker !== null && auth?.user?.user_id && (
                <BillTrackingModal
                    bill={bill}
                    billVersionId={selectedVersion?.bill_version_id}
                    userId={auth.user.user_id}
                    initialTracker={trackingModalTracker}
                    onClose={() => setTrackingModalTracker(null)}
                    onSaved={() => { setTrackingModalTracker(null); fetchTrackingData(); }}
                />
            )}

            {/* Analysis View Modal */}
            {analysisModalOpen && selectedAnalysis && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={closeAnalysisModal}>
                    <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header post-modal-header">
                                <span className="modal-title post-modal-title display-6">
                                    <span className="analysis-title">{bill.bill_type} {bill.bill_number} - {selectedAnalysis.committee_name || 'Analysis'}</span> ({formatAnalysisDate(selectedAnalysis.analysis_date)})
                                </span>
                            </div>
                            <div className="modal-body">
                                <div className="analysis-document" dangerouslySetInnerHTML={{ __html: selectedAnalysis.analysis_html || '<p class="text-muted">No content available.</p>' }} />
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn analysis-view-word btn-sm" onClick={() => downloadAnalysisAsWord(selectedAnalysis)}>
                                    <i className="bi bi-file-earmark-word me-1" />Word
                                </button>
                                <button type="button" className="btn analysis-view-pdf btn-sm" onClick={() => downloadAnalysisAsPDF(selectedAnalysis)}>
                                    <i className="bi bi-file-earmark-pdf me-1" />PDF
                                </button>
                                <button type="button" className="btn analysis-close btn-sm px-3" onClick={closeAnalysisModal}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Client Feedback Modal */}
            {feedbackModal && (
                <div className="hearing-modal-overlay" onClick={() => { setFeedbackModal(false); clearFeedbackFilters(); }}>
                    <div className="bp-feedback-modal" onClick={e => e.stopPropagation()}>
                        <div className="bp-feedback-modal-header">
                            <span>Client Feedback</span>
                            <span className="bp-feedback-modal-count">
                                {filteredClientFeedback.length === clientFeedback.length
                                    ? `${clientFeedback.length} response${clientFeedback.length !== 1 ? 's' : ''}`
                                    : `${filteredClientFeedback.length} of ${clientFeedback.length} response${clientFeedback.length !== 1 ? 's' : ''}`}
                            </span>
                        </div>
                        <div className="bp-feedback-modal-filters">
                            <select
                                className="bp-feedback-filter-select"
                                value={feedbackContactFilter}
                                onChange={(e) => setFeedbackContactFilter(e.target.value)}
                            >
                                <option value="">All Contacts</option>
                                {feedbackContactOptions.map(([contactId, name]) => (
                                    <option key={contactId} value={contactId}>{name}</option>
                                ))}
                            </select>
                            <select
                                className="bp-feedback-filter-select"
                                value={feedbackVersionFilter}
                                onChange={(e) => setFeedbackVersionFilter(e.target.value)}
                            >
                                <option value="">All Versions</option>
                                {sortedBillVersions.map((version) => (
                                    <option key={version.bill_version_id} value={version.bill_version_id}>
                                        {version.version_status} {formatVersionDate(version.createdAt)}
                                    </option>
                                ))}
                            </select>
                            {(feedbackContactFilter || feedbackVersionFilter) && (
                                <button type="button" className="bp-feedback-filter-clear" onClick={clearFeedbackFilters}>
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className="bp-feedback-modal-body">
                            {filteredClientFeedback.length === 0 ? (
                                <div className="bp-feedback-modal-empty">No feedback matches these filters.</div>
                            ) : filteredClientFeedback.map((resp, idx) => (
                                <div key={resp.id} className={`bp-feedback-modal-row${idx < filteredClientFeedback.length - 1 ? ' bp-feedback-modal-row--bordered' : ''}`}>
                                    <div className="bp-feedback-meta">
                                        <span className="bp-feedback-client">{resp.ClientFeedbackRequest?.TeamClient?.client_name}</span>
                                        <span className="bp-feedback-separator">·</span>
                                        <span className="bp-feedback-respondent">{resp.respondent_name}</span>
                                        <span className="bp-feedback-separator">·</span>
                                        <span className="bp-feedback-date">
                                            {new Date(resp.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                    </div>
                                    <div className="bp-feedback-notes">{renderFeedbackNotes(resp.notes)}</div>
                                </div>
                            ))}
                        </div>
                        <div className="bp-feedback-modal-footer">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setFeedbackModal(false); clearFeedbackFilters(); }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}